import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Users, Image, Paintbrush, Zap, FileText, Layers, ChevronRight } from 'lucide-react';
import { useOrgStore } from '../../stores/orgStore';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { cn } from '@/lib/utils';
import { BillingService, type SubscriptionSnapshotDto } from '@/services/BillingService';

interface OrgStats {
  totalMembers: number;
  pendingInvites: number;
  totalAssets: number;
  galleryItems: number;
  templatesCreated: number;
  monthlyUsage: {
    assetsCreated: number;
    aiGenerations: number;
    downloadsCount: number;
  };
}

interface QuickAction {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  color: string;
  comingSoon?: boolean;
}

export function OrgOverview() {
  const activeOrg = useOrgStore(s => s.orgs.find(org => org.id === s.activeOrgId));
  const [stats, setStats] = useState<OrgStats>({
    totalMembers: 0,
    pendingInvites: 0,
    totalAssets: 0,
    galleryItems: 0,
    templatesCreated: 0,
    monthlyUsage: {
      assetsCreated: 0,
      aiGenerations: 0,
      downloadsCount: 0
    }
  });
  const [recentActivity, setRecentActivity] = useState<Array<{ id: string; type: string; description: string; timestamp: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionSnapshotDto | null>(null);
  const billingService = React.useMemo(() => new BillingService(), []);

  useEffect(() => {
    const load = async () => {
      if (!activeOrg?.id) return;
      setLoading(true);
      try {
        const [statsRes, actRes] = await Promise.all([
          fetch(`/api/orgs/${encodeURIComponent(activeOrg.id)}/dashboard/stats`, { credentials: 'include', headers: { 'X-Organization-Id': activeOrg.id } }),
          fetch(`/api/orgs/${encodeURIComponent(activeOrg.id)}/dashboard/activity`, { credentials: 'include', headers: { 'X-Organization-Id': activeOrg.id } }),
        ]);
        const statsJson = await statsRes.json();
        const actJson = await actRes.json();
        if (statsRes.ok && statsJson?.success && statsJson.data) setStats(statsJson.data as OrgStats);
        if (actRes.ok && actJson?.success && Array.isArray(actJson.data)) setRecentActivity(actJson.data);
        
        // Fetch subscription data
        try {
          const sub = await billingService.getSubscription();
          setSubscription(sub);
        } catch (_err) {
          // silent; subscription might not exist
        }
      } catch (_err) {
        // silent; keep defaults
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [activeOrg?.id, billingService]);

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const absMs = Math.abs(diffInMs);
    const mins = Math.floor(absMs / (1000 * 60));
    const hours = Math.floor(absMs / (1000 * 60 * 60));
    const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
    const suffix = diffInMs >= 0 ? 'ago' : 'from now';
    if (mins < 60) return `${mins} minutes ${suffix}`;
    if (hours < 24) return `${hours} hours ${suffix}`;
    return `${days} days ${suffix}`;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'invite_sent':
        return (
          <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
            <Plus className="w-5 h-5 text-primary-600" />
          </div>
        );
      case 'member_joined':
        return (
          <div className="w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-secondary-600" />
          </div>
        );
      case 'asset_created':
        return (
          <div className="w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center">
            <Image className="w-5 h-5 text-secondary-600" />
          </div>
        );
      case 'gallery_uploaded':
        return (
          <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center">
            <Paintbrush className="w-5 h-5 text-accent-600" />
          </div>
        );
      case 'post_scheduled':
        return (
          <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-purple-600" />
          </div>
        );
      case 'template_created':
        return (
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-blue-600" />
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
            <Plus className="w-5 h-5 text-gray-500" />
          </div>
        );
    }
  };

  const quickActions: QuickAction[] = [
    {
      title: 'Manage Team',
      description: 'Invite members and manage roles',
      href: '/organization/members',
      color: 'bg-primary-50 text-primary-600 border-primary-200',
      icon: <Users className="w-6 h-6" />
    },
    {
      title: 'Gallery',
      description: 'Manage shared images and assets',
      href: '/organization/gallery',
      color: 'bg-secondary-50 text-secondary-600 border-secondary-200',
      icon: <Image className="w-6 h-6" />
    }
  ];

  if (!activeOrg) {
    return (
      <PageContainer>
        <div className="text-center">
          <div className="text-sm text-muted-foreground">No organization selected</div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={activeOrg.name}
        description="Organization overview and quick actions"
      >
        <div className="flex items-center gap-2">
          <Badge variant={activeOrg.status === 'active' ? 'success' : 'warning'}>
            {activeOrg.status}
          </Badge>
        </div>
      </PageHeader>
        
        {/* Organization Details */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8">
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <dt className="text-sm font-medium text-gray-500">Organization ID</dt>
              <dd className="text-sm text-gray-900 font-mono">{activeOrg.slug}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Plan</dt>
              <dd className="flex items-center gap-2">
                <span className="text-sm text-gray-900 font-medium">
                  {subscription?.plan?.name || activeOrg.plan_key || 'Free'}
                </span>
                {subscription?.status === 'trialing' && (
                  <Badge variant="default" className="text-xs">Trial</Badge>
                )}
                {subscription?.status === 'active' && (
                  <Badge variant="success" className="text-xs">Active</Badge>
                )}
                {subscription?.status === 'past_due' && (
                  <Badge variant="warning" className="text-xs">Past Due</Badge>
                )}
              </dd>
              {subscription?.currentPeriodEnd && (
                <dd className="text-xs text-gray-500 mt-1">
                  {subscription.status === 'trialing' ? 'Trial ends' : 'Renews'} {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                </dd>
              )}
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created</dt>
              <dd className="text-sm text-gray-900">{activeOrg.created_at ? new Date(activeOrg.created_at).toLocaleDateString() : 'N/A'}</dd>
            </div>
          </dl>
        </div>

        {/* Stats Overview */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Organization Stats</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Team Members</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalMembers}</p>
                {stats.pendingInvites > 0 && (
                  <p className="text-xs text-warning-600 mt-1">{stats.pendingInvites} pending</p>
                )}
              </div>
              <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-primary-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Assets</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalAssets}</p>
                <p className="text-xs text-success-600 mt-1">+{stats.monthlyUsage.assetsCreated} this month</p>
              </div>
              <div className="w-10 h-10 bg-secondary-50 rounded-lg flex items-center justify-center">
                <Image className="w-5 h-5 text-secondary-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Gallery Items</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.galleryItems}</p>
              </div>
              <div className="w-10 h-10 bg-accent-50 rounded-lg flex items-center justify-center">
                <Layers className="w-5 h-5 text-accent-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">AI Generations</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stats.monthlyUsage.aiGenerations}</p>
                <p className="text-xs text-gray-500 mt-1">this month</p>
              </div>
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {quickActions.map((action) => {
            if (action.comingSoon) {
              return (
                <div
                  key={action.href}
                  className="block bg-white rounded-lg border p-6 transition-all opacity-60 cursor-not-allowed"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 ${action.color} rounded-lg flex items-center justify-center`}>
                      {action.icon}
                    </div>
                    <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{action.title}</h3>
                  <p className="text-sm text-gray-600">{action.description}</p>
                </div>
              );
            }

            return (
              <Link
                key={action.href}
                to={action.href}
                className="block bg-white rounded-lg border p-6 transition-all hover:shadow-md hover:border-gray-300"
              >
                <div className="flex items-start gap-4">
                  <div className={cn('p-3 rounded-lg border', action.color)}>
                    {action.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-gray-900 mb-1">
                      {action.title}
                    </h3>
                    <p className="text-sm text-gray-600">{action.description}</p>
                  </div>
                  <div className="text-gray-400">
                    <ChevronRight className="w-5 h-5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="space-y-4">
            {recentActivity.length === 0 && !loading && (
              <div className="text-sm text-gray-500">No recent activity.</div>
            )}
            {recentActivity.map((a) => (
              <div key={a.id} className="flex items-center gap-4 pb-4 last:pb-0 border-b last:border-b-0 border-gray-100">
                {getActivityIcon(a.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">{a.description}</p>
                  <p className="text-xs text-gray-500 mt-1">{formatTimeAgo(new Date(a.timestamp))}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-gray-100">
            <Button variant="ghost" size="sm" className="w-full">
              View all activity
              <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                Coming Soon
              </span>
            </Button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
